import { useState, useRef, useEffect } from "react";
import {
  House, Sparkle, ChartBar, List, TrendUp,
  CurrencyDollar, Crown, UserCircle, Gear, Globe, ChatCircleDots, BookOpen, Newspaper, Briefcase,
  CaretDown, NotePencil, Megaphone, Storefront,
} from "@phosphor-icons/react";
import { LuminaLogo, PrimePillarWordmark } from "../LandingPage";
import LLMHealthIndicator from "./LLMHealthIndicator";

// Utility tabs collapsed into a "More" dropdown so the desktop nav stays clean.
const MORE_TABS = [
  { tab: "jobs", label: "YC Jobs", icon: Briefcase, title: "YC Jobs — who's hiring and for what" },
  { tab: "reports", label: "Weekly Reports", icon: Newspaper, title: "Weekly Founder Report — rising opportunities from your data" },
  { tab: "guide", label: "Guide & Demo", icon: BookOpen, title: "How everything works + how to get the best results" },
  { tab: "briefs", label: "My Briefs", icon: NotePencil, title: "MVP briefs you've saved to your library" },
  { tab: "landing", label: "Home", icon: House, title: "Back to home page" },
];

// Top application header: brand + desktop grouped nav (lg+) and the slim
// mobile header cluster (logo + upgrade/account + settings). All tab routing
// is driven through the `setActiveTab` prop so the shell stays stateless.
const AppHeader = ({ user, activeTab, setActiveTab, setShowSettings, openAuth }) => {
  const isAdmin = typeof window !== "undefined" && !!localStorage.getItem("pp_admin_token");
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    if (!moreOpen) return;
    const onDocDown = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setMoreOpen(false); };
    document.addEventListener("pointerdown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDocDown); document.removeEventListener("keydown", onKey); };
  }, [moreOpen]);

  const navBtn = (tab) =>
    `px-2 xl:px-3 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
      activeTab === tab
        ? "bg-violet-600/10 text-violet-700 ring-1 ring-violet-200"
        : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"
    }`;

  const moreActive = MORE_TABS.some((m) => m.tab === activeTab);

  return (
    <header className="lum-glass border-b border-white/8 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 cursor-pointer" onClick={() => setActiveTab("landing")} data-testid="logo-home-link" title="Back to home page">
            <LuminaLogo size={48} className="flex-shrink-0" testid="brand-logo" />
            <PrimePillarWordmark height={32} className="hidden sm:block" testid="brand-wordmark" />
            <h1 className="sm:hidden text-base font-semibold tracking-tight text-zinc-900 truncate" data-testid="app-title">
              Prime Pillar
            </h1>
          </div>
          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1.5 flex-shrink-0">
            <LLMHealthIndicator />

            {/* Ask AI — flagship grounded research co-pilot */}
            <button
              onClick={() => setActiveTab("copilot")}
              className={`px-2 xl:px-3 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap ${
                activeTab === "copilot"
                  ? "bg-violet-600 text-white"
                  : "text-violet-700 bg-violet-50 hover:bg-violet-100 ring-1 ring-violet-200"
              }`}
              data-testid="nav-copilot"
              title="Ask the AI Research Co-Pilot anything — grounded in real data"
              aria-label="Ask AI"
            >
              <ChatCircleDots size={15} className="xl:mr-1 inline" weight="fill" /> <span className="hidden xl:inline">Advisor</span>
            </button>

            {/* FAMILY 1 — Proven Winners (the defensible engine — led first) */}
            <div className="flex items-center gap-0.5 pr-1 mr-0.5 border-r border-zinc-200">
              <span className="hidden 2xl:inline text-[9px] font-mono-data uppercase tracking-[0.18em] text-zinc-400 px-1.5 select-none" data-testid="nav-group-proven">Proven Winners</span>
              <button onClick={() => setActiveTab("discover")} className={navBtn("discover")} data-testid="nav-discover" title="216 ranked, grounded opportunities — proven models to clone" aria-label="Discover">
                <Sparkle size={15} className="xl:mr-1 inline" weight="duotone" /> <span className="hidden xl:inline">Discover</span>
              </button>
              <button onClick={() => setActiveTab("evaluate")} className={navBtn("evaluate")} data-testid="nav-evaluate" title="Per-company teardown: 4 lenses + synthesis" aria-label="Evaluate">
                <Sparkle size={15} className="xl:mr-1 inline" weight="bold" /> <span className="hidden xl:inline">Evaluate</span>
              </button>
            </div>

            {/* FAMILY 2 — Demand Signals (the live scanner) */}
            <div className="flex items-center gap-0.5 pr-1 mr-0.5 border-r border-zinc-200">
              <span className="hidden 2xl:inline text-[9px] font-mono-data uppercase tracking-[0.18em] text-zinc-400 px-1.5 select-none" data-testid="nav-group-signals">Demand Signals</span>
              <button onClick={() => setActiveTab("dashboard")} className={navBtn("dashboard")} data-testid="nav-dashboard" aria-label="Dashboard">
                <ChartBar size={15} className="xl:mr-1 inline" weight="duotone" /> <span className="hidden xl:inline">Dashboard</span>
              </button>
              <button onClick={() => setActiveTab("opportunities")} className={navBtn("opportunities")} data-testid="nav-opportunities" aria-label="Signals">
                <List size={15} className="xl:mr-1 inline" weight="duotone" /> <span className="hidden xl:inline">Signals</span>
              </button>
              <button onClick={() => setActiveTab("pain_points")} className={navBtn("pain_points")} data-testid="nav-pain-points" title="Real user complaints from Reddit — pain worth solving" aria-label="Pain Points">
                <Megaphone size={15} className="xl:mr-1 inline" weight="duotone" /> <span className="hidden xl:inline">Pain Points</span>
              </button>
              <button onClick={() => setActiveTab("ai_pulse")} className={navBtn("ai_pulse")} data-testid="nav-ai-pulse" aria-label="AI Pulse">
                <Sparkle size={15} className="xl:mr-1 inline" weight="fill" /> <span className="hidden xl:inline">AI Pulse</span>
              </button>
              <button onClick={() => setActiveTab("trending")} className={navBtn("trending")} data-testid="nav-trending" aria-label="Trending">
                <TrendUp size={15} className="xl:mr-1 inline" weight="duotone" /> <span className="hidden xl:inline">Trending</span>
              </button>
              <button onClick={() => setActiveTab("country_fit")} className={navBtn("country_fit")} data-testid="nav-country-fit" title="Best country to launch each opportunity in" aria-label="Best Country">
                <Globe size={15} className="xl:mr-1 inline" weight="duotone" /> <span className="hidden xl:inline">Best Country</span>
              </button>
            </div>

            {/* MORE — utility tabs collapsed into a dropdown to keep the bar clean */}
            <button onClick={() => setActiveTab("commerce")} className={`relative ${navBtn("commerce")}`} data-testid="nav-commerce" title="Commerce Intelligence — 94 real customer craving clusters, demand validation & AI review" aria-label="Commerce">
              <Storefront size={15} className="lg:mr-1 inline" weight="duotone" /> <span className="hidden lg:inline">Commerce</span>
              <span className="absolute top-1 right-1 xl:right-2 w-1.5 h-1.5 rounded-full bg-violet-500 ring-2 ring-white" aria-hidden="true" />
            </button>

            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setMoreOpen((v) => !v)}
                className={`px-2 xl:px-3 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap inline-flex items-center gap-1 ${
                  moreActive || moreOpen ? "bg-violet-600/10 text-violet-700 ring-1 ring-violet-200" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"
                }`}
                data-testid="nav-more"
                aria-haspopup="true"
                aria-expanded={moreOpen}
              >
                More <CaretDown size={12} weight="bold" className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-white shadow-xl ring-1 ring-zinc-200 p-1.5 z-50" data-testid="nav-more-menu">
                  {MORE_TABS.map((m) => {
                    const Icon = m.icon;
                    const active = activeTab === m.tab;
                    return (
                      <button
                        key={m.tab}
                        onClick={() => { setActiveTab(m.tab); setMoreOpen(false); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-colors ${active ? "bg-violet-600/10 text-violet-700" : "text-zinc-600 hover:bg-zinc-50"}`}
                        data-testid={`nav-more-${m.tab}`}
                        title={m.title}
                      >
                        <Icon size={17} weight="duotone" /> {m.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button onClick={() => setActiveTab("pricing")} className={navBtn("pricing")} data-testid="nav-pricing" aria-label="Pricing">
              <span className="hidden xl:inline">Pricing</span>
              <CurrencyDollar size={15} className="xl:hidden" weight="duotone" />
            </button>
            {user ? (
              <>
                {user.tier === "free" && (
                  <button
                    onClick={() => setActiveTab("pricing")}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold bg-violet-600 text-white rounded-full hover:bg-violet-500 transition-all p2p-cta-pulse whitespace-nowrap"
                    data-testid="header-upgrade-btn"
                  >
                    <Crown size={12} weight="fill" /> Upgrade
                  </button>
                )}
                <button
                  onClick={() => setActiveTab("account")}
                  className={`px-2 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === "account" ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'}`}
                  data-testid="nav-account"
                  title={user.email}
                >
                  <UserCircle size={20} weight="duotone" />
                </button>
              </>
            ) : (
              <button
                onClick={() => openAuth("login")}
                className="ml-1 px-4 py-2 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-500 rounded-full transition-all shadow-[0_0_18px_-4px_rgba(255,107,26,0.5)] whitespace-nowrap"
                data-testid="nav-login-btn"
              >
                Get started
              </button>
            )}
            {isAdmin && (
              <button onClick={() => setShowSettings(true)} className="p-2 text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="nav-settings">
                <Gear size={16} weight="duotone" />
              </button>
            )}
          </nav>

          {/* Mobile header cluster (lg:hidden) — logo + upgrade + account + settings */}
          <div className="flex lg:hidden items-center gap-1">
            {user && user.tier === "free" && (
              <button onClick={() => setActiveTab("pricing")} className="inline-flex items-center gap-1 px-3 min-h-[40px] text-xs font-semibold bg-violet-600 text-white rounded-full" data-testid="mobile-upgrade-btn">
                <Crown size={12} weight="fill" /> Upgrade
              </button>
            )}
            {user ? (
              <button onClick={() => setActiveTab("account")} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-zinc-600" data-testid="mobile-account-btn" aria-label="Account">
                <UserCircle size={26} weight="duotone" />
              </button>
            ) : (
              <button onClick={() => openAuth("login")} className="px-4 min-h-[40px] text-sm font-semibold text-white bg-violet-600 rounded-full" data-testid="mobile-login-btn">
                Get started
              </button>
            )}
            {isAdmin && (
              <button onClick={() => setShowSettings(true)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-zinc-500" data-testid="mobile-settings-btn" aria-label="Settings">
                <Gear size={20} weight="duotone" />
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
